import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Rating,
  Chip,
  IconButton,
  useTheme,
  Avatar,
  Stack,
  CircularProgress,
  Alert,
  Grow,
} from '@mui/material';
import {
  LocationOn,
  AccessTime,
  Person,
  KeyboardArrowDown,
  Refresh,
} from '@mui/icons-material';
import BookMyOpAPI from '../../services/BookMyOpapi';

const DoctorsList = ({ dateFilter, sortOrder = '-star_rating' }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleDoctors, setVisibleDoctors] = useState(4); // Show 4 initially
  const [showingMore, setShowingMore] = useState(false);

  // Fetch high-rated doctors data from API
  const fetchDoctors = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build filters
      const filters = {
        page: 1,
        pageSize: 12,
        ordering: sortOrder, // Use sortOrder prop
      };
      
      // Add date filters if provided
      if (dateFilter) {
        if (dateFilter.availableDate) {
          filters.availableDate = dateFilter.availableDate;
        } else if (dateFilter.availableDateStart || dateFilter.availableDateEnd) {
          if (dateFilter.availableDateStart) {
            filters.availableDateStart = dateFilter.availableDateStart;
          }
          if (dateFilter.availableDateEnd) {
            filters.availableDateEnd = dateFilter.availableDateEnd;
          }
        }
      }
      
      // Use getDoctors API which supports date filters
      const response = await BookMyOpAPI.getDoctors(filters);
      
      if (response && response.results) {
        // Filter for high-rated doctors (rating >= 4.0) and get first 12
        const highRatedDoctors = response.results
          .filter(doctor => doctor.star_rating && doctor.star_rating >= 4.0)
          .slice(0, 12);
        setDoctors(highRatedDoctors);
        setError(null);
      } else if (Array.isArray(response)) {
        const highRatedDoctors = response
          .filter(doctor => doctor.star_rating && doctor.star_rating >= 4.0)
          .slice(0, 12);
        setDoctors(highRatedDoctors);
        setError(null);
      } else {
        setError('No doctors found');
        setDoctors([]);
      }
    } catch (err) {
      setError('Failed to fetch doctors. Please try again later.');
      console.error('Error fetching doctors:', err);
    } finally {
      setLoading(false);
    }
  }, [dateFilter, sortOrder]);

  // Fetch doctors when component mounts or dateFilter changes
  useEffect(() => {
    fetchDoctors();
  }, [fetchDoctors]);

  const handleBookAppointment = (doctorId) => {
    const params = new URLSearchParams();
    if (dateFilter) {
      if (dateFilter.availableDate) {
        params.set('availableDate', dateFilter.availableDate);
      } else {
        if (dateFilter.availableDateStart) {
          params.set('availableDateStart', dateFilter.availableDateStart);
        }
        if (dateFilter.availableDateEnd) {
          params.set('availableDateEnd', dateFilter.availableDateEnd);
        }
      }
    }
    const queryString = params.toString();
    navigate(`/doctors/${doctorId}${queryString ? `?${queryString}` : ''}`);
  };

  const handleShowMore = () => {
    setShowingMore(true);
    setTimeout(() => {
      setVisibleDoctors(prev => Math.min(prev + 4, doctors.length));
      setShowingMore(false);
    }, 300);
  };

  const handleRefresh = () => {
    setVisibleDoctors(4);
  };

  const DoctorCard = ({ doctor, index }) => {
    const rating = doctor.star_rating; // Use API star_rating directly

    return (
      <Grow
        in={true}
        timeout={500 + (index % 4) * 200}
        style={{ transformOrigin: '0 0 0' }}
      >
        <Card
          sx={{
            height: { xs: 180, md: 190 },
            borderRadius: 2.5,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease-in-out',
            cursor: 'pointer',
            overflow: 'hidden',
            position: 'relative',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid rgba(0,0,0,0.06)',
            '&:hover': {
              transform: 'translateY(-8px) scale(1.02)',
              boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
              zIndex: 10,
              '& .doctor-avatar': {
                transform: 'scale(1.1)',
              },
            },
          }}
          onClick={() => handleBookAppointment(doctor.id)}
        >
          <CardContent sx={{ p: { xs: 1.5, md: 2 }, height: '100%', display: 'flex', gap: { xs: 1.5, md: 2 } }}>
            {/* Doctor Avatar/Image */}
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
              {doctor.profile_photo ? (
                <Avatar
                  src={doctor.profile_photo}
                  alt={doctor.name}
                  className="doctor-avatar"
                  sx={{
                    width: { xs: 50, md: 60 },
                    height: { xs: 50, md: 60 },
                    border: '2px solid #fff',
                    boxShadow: '0 3px 10px rgba(0,0,0,0.15)',
                    transition: 'transform 0.3s ease',
                  }}
                />
              ) : (
                <Avatar
                  className="doctor-avatar"
                  sx={{
                    width: { xs: 50, md: 60 },
                    height: { xs: 50, md: 60 },
                    bgcolor: 'primary.main',
                    border: '2px solid #fff',
                    boxShadow: '0 3px 10px rgba(0,0,0,0.15)',
                    transition: 'transform 0.3s ease',
                  }}
                >
                  <Person sx={{ fontSize: { xs: 22, md: 28 } }} />
                </Avatar>
              )}
            </Box>

            {/* Doctor Info */}
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              {/* Header - Doctor Name & Specialty */}
              <Box sx={{ mb: { xs: 0.5, md: 0.8 } }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 800, 
                    fontSize: { xs: '1.1rem', md: '1.2rem' },
                    lineHeight: 1.1,
                    mb: 0.2,
                    color: '#1a1a1a',
                  }}
                >
                  {doctor.name}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="primary" 
                  sx={{ 
                    fontWeight: 700, 
                    fontSize: { xs: '0.85rem', md: '0.95rem' },
                    lineHeight: 1.1,
                  }}
                >
                  {doctor.specialty_name}
                </Typography>
              </Box>

              {/* Hospital Name & Distance */}
              <Box sx={{ mb: { xs: 0.5, md: 0.8 } }}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <LocationOn sx={{ fontSize: { xs: 14, md: 16 }, color: '#666' }} />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: { xs: '0.85rem', md: '0.9rem' },
                      fontWeight: 700,
                      color: '#666',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {doctor.service_provider_name}
                  </Typography>
                  {doctor.distance_km && (
                    <Chip
                      label={`${doctor.distance_km} km`}
                      size="small"
                      sx={{
                        height: { xs: 18, md: 20 },
                        fontSize: { xs: '0.6rem', md: '0.65rem' },
                        bgcolor: '#e3f2fd',
                        color: '#1976d2',
                        fontWeight: 700,
                        ml: 'auto !important',
                      }}
                    />
                  )}
                </Stack>
              </Box>

              {/* Rating & Experience */}
              <Box sx={{ mb: { xs: 0.8, md: 1 } }}>
                {/* Only show rating if star_rating is not null */}
                {rating !== null && (
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.3 }}>
                    <Rating 
                      value={rating} 
                      precision={0.1} 
                      size="small" 
                      readOnly 
                      sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }} 
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' }, fontWeight: 700 }}>
                      {rating}
                    </Typography>
                  </Stack>
                )}
                
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <AccessTime sx={{ fontSize: { xs: 12, md: 14 }, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' }, fontWeight: 700 }}>
                    {doctor.experience_years}y exp
                  </Typography>
                </Stack>
                
                {/* Qualifications */}
                {doctor.qualifications && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', md: '0.7rem' }, fontWeight: 600, display: 'block', mt: 0.2 }}>
                    {doctor.qualifications.name || doctor.qualifications.abbreviation}
                  </Typography>
                )}
              </Box>

              {/* Footer - Price & Book Button */}
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 'auto' }}>
                <Box>
                  <Typography 
                    variant="h6" 
                    color="primary" 
                    sx={{ 
                      fontWeight: 800, 
                      fontSize: { xs: '1rem', md: '1.1rem' },
                      lineHeight: 1,
                    }}
                  >
                    ₹{doctor.consultation_fee}
                  </Typography>
                  {doctor.discount_percentage && parseFloat(doctor.discount_percentage) > 0 && (
                    <Typography 
                      variant="caption" 
                      color="error" 
                      sx={{ 
                        fontWeight: 700, 
                        fontSize: { xs: '0.6rem', md: '0.65rem' },
                        display: 'block',
                        lineHeight: 1,
                      }}
                    >
                      {doctor.discount_percentage}% off
                    </Typography>
                  )}
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: { xs: '0.75rem', md: '0.8rem' },
                    px: { xs: 1.5, md: 2 },
                    py: { xs: 0.4, md: 0.5 },
                    minWidth: { xs: '60px', md: '70px' },
                    height: { xs: '30px', md: '34px' },
                    boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBookAppointment(doctor.id);
                  }}
                >
                  Book
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Grow>
    );
  };

  if (loading) {
    return (
      <Box sx={{ py: { xs: 3, md: 4 }, bgcolor: '#f8fafc' }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <CircularProgress size={50} />
          </Box>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: { xs: 3, md: 4 }, bgcolor: '#f8fafc' }}>
        <Container maxWidth="xl">
          <Alert severity="error" sx={{ maxWidth: 600, mx: 'auto' }}>
            {error}
          </Alert>
        </Container>
      </Box>
    );
  }

  if (doctors.length === 0) {
    return (
      <Box sx={{ py: { xs: 3, md: 4 }, bgcolor: '#f8fafc' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No doctors available at the moment.
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ }}>
      <Container maxWidth="xxl">
        <Stack direction="row" justifyContent="center" alignItems="center" sx={{ mb: { xs: 2, md: 3 },  }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              color: theme.palette.text.primary,
        
              textTransform:'uppercase',
              textAlign:'center',
            }}
          >
            Top Doctors Near You
          </Typography>
          
          {visibleDoctors > 4 && (
            <IconButton
              onClick={handleRefresh}
              size="small"
              sx={{
                bgcolor: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                '&:hover': { bgcolor: 'grey.50' },
              }}
            >
              <Refresh sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </Stack>

        {/* Responsive Grid Layout */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: { xs: 2, md: 2.5 },
            px: { xs: 1, md: 2 },
          }}
        >
          {doctors.slice(0, visibleDoctors).map((doctor, index) => (
            <DoctorCard key={doctor.id} doctor={doctor} index={index} />
          ))}
        </Box>

        {/* Show More / View All Actions */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          {visibleDoctors < doctors.length ? (
            <Button
              variant="contained"
              onClick={handleShowMore}
              disabled={showingMore}
              startIcon={showingMore ? <CircularProgress size={16} /> : <KeyboardArrowDown />}
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 600,
                // px: 4,
                // py: 1.5,
                fontSize: { xs: '0.7rem', md: '0.75rem' },
                mr: 2,
                background: 'linear-gradient(45deg, theme.palette.primary.main, theme.palette.secondary.main)',
                '&:hover': {
                  background: 'linear-gradient(45deg, theme.palette.primary.main, theme.palette.secondary.main)',
                },
              }}
            >
              {showingMore ? 'Loading...' : `Show More (${doctors.length - visibleDoctors} left)`}
            </Button>
          ) : null}
          
          <Button
            variant="outlined"
            onClick={() => {
              const params = new URLSearchParams();
              if (dateFilter) {
                if (dateFilter.availableDate) {
                  params.set('availableDate', dateFilter.availableDate);
                } else {
                  if (dateFilter.availableDateStart) {
                    params.set('availableDateStart', dateFilter.availableDateStart);
                  }
                  if (dateFilter.availableDateEnd) {
                    params.set('availableDateEnd', dateFilter.availableDateEnd);
                  }
                }
              }
              const queryString = params.toString();
              navigate(`/find-doctors${queryString ? `?${queryString}` : ''}`);
            }}
            sx={{
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: { xs: '0.7rem', md: '0.75rem' },
              // px: 4,
              // py: 1.5,
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              '&:hover': {
                bgcolor: theme.palette.primary.main,
                color: 'white',
              },
            }}
          >
            View All Doctors
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default DoctorsList; 